import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Subject, merge } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-portal-navbar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <nav class="navbar">
      <div class="navbar-inner">

        <!-- Brand -->
        <a routerLink="/" class="brand">
          <div class="brand-badge">UTB</div>
          <span class="brand-label">Portal</span>
        </a>

        <!-- Left nav tabs — authenticated only -->
        @if (isLoggedIn()) {
          <div class="nav-tabs">
            @if (isSiteAdmin() || isOrgAdmin() || isEventPlanner()) {
              <a routerLink="/events"           routerLinkActive="nav-tab--active" class="nav-tab">Events</a>
              <a routerLink="/teams"            routerLinkActive="nav-tab--active" class="nav-tab">Teams</a>
              <a routerLink="/questions"        routerLinkActive="nav-tab--active" class="nav-tab">Questions</a>
            }
            @if (isSiteAdmin() || isOrgAdmin() || isEventPlanner()) {
              <a routerLink="/game-preferences" routerLinkActive="nav-tab--active" class="nav-tab">Game Preferences</a>
            }
            @if (isSiteAdmin() || isOrgAdmin() || isEventPlanner() || isTeamMember()) {
              <a routerLink="/my-media"       routerLinkActive="nav-tab--active" class="nav-tab">My Media</a>
            }
            @if (isSiteAdmin()) {
              <a routerLink="/organizations"  routerLinkActive="nav-tab--active" class="nav-tab">Organizations</a>
            }
            @if (isSiteAdmin() || isOrgAdmin() || isEventPlanner()) {
              <a routerLink="/my-cart"       routerLinkActive="nav-tab--active" class="nav-tab">My Cart</a>
            }
            @if (isSiteAdmin()) {
              <a routerLink="/invitations"    routerLinkActive="nav-tab--active" class="nav-tab">Invitations</a>
            }
            @if (isOrgAdmin() && !isSiteAdmin()) {
              <a routerLink="/org-invitations" routerLinkActive="nav-tab--active" class="nav-tab">Invitations</a>
            }
            @if (isSiteAdmin() || isOrgAdmin()) {
              <div class="nav-utilities" tabindex="0">
                <span class="nav-tab nav-tab--group">Utilities</span>
                <div class="utilities-menu">
                  <a routerLink="/utilities/events" routerLinkActive="utility-link--active" class="utility-link">Events</a>
                  <a routerLink="/utilities/teams" routerLinkActive="utility-link--active" class="utility-link">Teams</a>
                  <a routerLink="/utilities/players" routerLinkActive="utility-link--active" class="utility-link">Players</a>
                  <a routerLink="/utilities/questions" routerLinkActive="utility-link--active" class="utility-link">Questions</a>
                  <a routerLink="/users" routerLinkActive="utility-link--active" class="utility-link">Users</a>
                  @if (isSiteAdmin()) {
                    <a routerLink="/products" routerLinkActive="utility-link--active" class="utility-link">Products</a>
                    <a routerLink="/utilities/themes" routerLinkActive="utility-link--active" class="utility-link">Themes</a>
                    <a routerLink="/utilities/invoices" routerLinkActive="utility-link--active" class="utility-link">Invoices</a>
                    <a routerLink="/payment-services" routerLinkActive="utility-link--active" class="utility-link">Payment Services</a>
                    <a routerLink="/utilities/organizations" routerLinkActive="utility-link--active" class="utility-link">Organizations Utilities</a>
                    <a routerLink="/utilities/data-health-dashboard" routerLinkActive="utility-link--active" class="utility-link">Data Health Dashboard</a>
                    <a routerLink="/game-events" routerLinkActive="utility-link--active" class="utility-link">Game Events</a>
                  }
                </div>
              </div>
            }
          </div>
        }

        <span class="spacer"></span>

        <!-- Right auth controls -->
        <div class="auth-row">
          <button
            class="btn-login"
            [class.btn--disabled]="isLoggedIn()"
            [disabled]="isLoggedIn()"
            (click)="login()"
          >Login</button>

          <button
            class="btn-logout"
            [class.btn--disabled]="!isLoggedIn()"
            [disabled]="!isLoggedIn()"
            (click)="logout()"
          >Logout</button>

          @if (isLoggedIn()) {
            <button class="btn-gear" title="Site Configuration" aria-label="Site Configuration">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15"
                   viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83
                         2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33
                         1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09
                         A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06
                         a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06
                         A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3
                         a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9
                         a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83
                         2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68
                         a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09
                         a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06
                         a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06
                         A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21
                         a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </button>
          }
        </div>

      </div>
    </nav>
  `,
  styles: [`
    .navbar {
      backdrop-filter: blur(14px);
      background: color-mix(in srgb, var(--color-surface-1) 96%, transparent);
      border-bottom: 1px solid var(--color-border);
      position: sticky;
      top: 0;
      z-index: 100;
    }

    .navbar-inner {
      align-items: center;
      display: flex;
      gap: 0.25rem;
      margin: 0 auto;
      max-width: 1280px;
      padding: 0.65rem 1.5rem;
    }

    /* Brand */
    .brand {
      align-items: center;
      display: flex;
      gap: 0.45rem;
      margin-right: 1.25rem;
      text-decoration: none;
    }

    .brand-badge {
      background: var(--color-accent);
      border-radius: 0.35rem;
      color: #fff;
      font-size: 0.62rem;
      font-weight: 800;
      letter-spacing: 0.07em;
      padding: 0.14rem 0.4rem;
    }

    .brand-label {
      color: var(--color-accent-soft);
      font-size: 0.95rem;
      font-weight: 700;
    }

    /* Nav tabs */
    .nav-tabs {
      align-items: center;
      display: flex;
      gap: 0.1rem;
    }

    .nav-tab {
      border-radius: 0.45rem;
      color: var(--color-muted);
      font-size: 0.85rem;
      font-weight: 500;
      padding: 0.35rem 0.8rem;
      text-decoration: none;
      transition: background 0.15s, color 0.15s;
    }

    .nav-tab:hover,
    .nav-tab--active {
      background: color-mix(in srgb, var(--color-accent) 14%, transparent);
      color: var(--color-text);
    }

    .nav-tab--group {
      cursor: default;
      display: inline-flex;
      user-select: none;
    }

    .nav-utilities {
      padding-bottom: 0.25rem;
      position: relative;
    }

    .nav-utilities:focus {
      outline: none;
    }

    .utilities-menu {
      background: color-mix(in srgb, var(--color-surface-1) 98%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.6rem;
      box-shadow: 0 10px 28px rgba(0, 0, 0, 0.2);
      display: none;
      flex-direction: column;
      gap: 0.05rem;
      left: 0;
      min-width: 210px;
      padding: 0.4rem;
      position: absolute;
      top: 100%;
      z-index: 101;
    }

    .nav-utilities:hover .utilities-menu,
    .nav-utilities:focus-within .utilities-menu {
      display: flex;
    }

    .utility-link {
      border-radius: 0.45rem;
      color: var(--color-muted);
      font-size: 0.83rem;
      font-weight: 500;
      padding: 0.38rem 0.55rem;
      text-decoration: none;
      transition: background 0.15s, color 0.15s;
      white-space: nowrap;
    }

    .utility-link:hover,
    .utility-link--active {
      background: color-mix(in srgb, var(--color-accent) 16%, transparent);
      color: var(--color-text);
    }

    .spacer { flex: 1; }

    /* Auth controls */
    .auth-row {
      align-items: center;
      display: flex;
      gap: 0.4rem;
    }

    .btn-login,
    .btn-logout {
      border-radius: 0.45rem;
      cursor: pointer;
      font-size: 0.8rem;
      font-weight: 600;
      padding: 0.35rem 0.85rem;
      transition: opacity 0.15s;
    }

    .btn-login {
      background: var(--color-accent);
      border: 1px solid var(--color-accent);
      color: #fff;
    }

    .btn-login:hover:not(:disabled) {
      background: color-mix(in srgb, var(--color-accent) 80%, #fff 20%);
    }

    .btn-logout {
      background: transparent;
      border: 1px solid var(--color-border);
      color: var(--color-muted);
    }

    .btn-logout:hover:not(:disabled) {
      background: color-mix(in srgb, var(--color-surface-2) 70%, transparent);
      color: var(--color-text);
    }

    .btn-login.btn--disabled,
    .btn-logout.btn--disabled,
    .btn-login:disabled,
    .btn-logout:disabled {
      cursor: not-allowed;
      opacity: 0.3;
    }

    /* Gear button */
    .btn-gear {
      align-items: center;
      background: transparent;
      border: 1px solid var(--color-border);
      border-radius: 0.45rem;
      color: var(--color-muted);
      cursor: pointer;
      display: flex;
      height: 31px;
      justify-content: center;
      transition: background 0.15s, color 0.15s;
      width: 31px;
    }

    .btn-gear:hover {
      background: color-mix(in srgb, var(--color-accent) 14%, transparent);
      color: var(--color-accent-soft);
    }
  `]
})
export class PortalNavbarComponent implements OnInit, OnDestroy {
  private readonly msalService          = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly authService          = inject(AuthService);
  private readonly router               = inject(Router);
  private readonly destroying$          = new Subject<void>();

  readonly isLoggedIn = signal(false);
  readonly isSiteAdmin = signal(false);
  readonly isOrgAdmin = signal(false);
  readonly isEventPlanner = signal(false);
  readonly isTeamMember = signal(false);

  ngOnInit(): void {
    void this.checkAuth();
    merge(
      this.msalBroadcastService.inProgress$.pipe(filter(s => s === InteractionStatus.None)),
      this.authService.simStateChanged$
    ).pipe(
      takeUntil(this.destroying$)
    ).subscribe(() => void this.checkAuth());
  }

  private async checkAuth(): Promise<void> {
    const loggedIn = this.authService.getActiveAccount() !== null;
    this.isLoggedIn.set(loggedIn);

    if (!loggedIn) {
      this.isSiteAdmin.set(false);
      this.isOrgAdmin.set(false);
      this.isEventPlanner.set(false);
      this.isTeamMember.set(false);
      return;
    }

    const roles = await this.authService.getRolesAsync();
    this.isSiteAdmin.set(roles.includes('SiteAdmin'));
    this.isOrgAdmin.set(roles.includes('OrgAdmin'));
    this.isEventPlanner.set(roles.includes('EventPlanner'));
    this.isTeamMember.set(roles.includes('TeamMember'));
  }

  login(): void {
    if (environment.devSimulateAuth) {
      this.authService.simLogin();
      return;
    }
    void this.msalService.loginRedirect();
  }

  logout(): void {
    if (environment.devSimulateAuth) {
      this.authService.simLogout();
      void this.router.navigate(['/']);
      return;
    }
    void this.msalService.logoutRedirect({ postLogoutRedirectUri: '/' });
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }
}
